import React, {useCallback, useContext, useEffect, useState, useRef, useMemo} from 'react';
import classnames from 'classnames';
import {List} from 'immutable';
import {
  List as VirtualizedList,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache
} from 'react-virtualized';
import {debounce} from 'lodash';
import {easeInOutCirc} from 'js-easing-functions';
import {upperFirst, pluralize} from 'lib/stringUtils';
import {useTracking} from 'react-tracking';

import {
  addToast,
  Button,
  Card,
  Dropdown,
  IconButton,
  ListGroup,
  ListGroupItem,
  LoadingSpinner,
  Text,
  WithToggle,
  Icon,
  SortableContainerContext,
  SortableContainerProvider,
  SortableElement,
  WithTooltip
} from '@albert-io/atomic';

import getDifficultyValueFromInt from 'lib/getDifficultyValueFromInt';
import {genericMandarkRequest, invalidatePartialInterest} from 'resources/mandark.resource';
import {getDraftQuestionSetsQuery} from 'client/components/ContentDiscovery/ContentDiscovery.queries';

import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import QuestionsListFooter from 'components/PracticeView/QuestionsList/QuestionsListFooter.react';

import {
  Question,
  QuestionList
} from 'client/Classrooms/Classroom/Teacher/Assignments/ViewStudentSubmission/ViewStudentSubmission.types';

import {ContentDiscoveryContext} from '../../ContentDiscoveryProvider.react';

// TODO: Make these styles more relative to both question lists
import '../../QuestionPreview/content-discovery-question-list.scss';

import './content-discovery-review-questions.scss';

interface Props {
  activeQuestion: Question;
  scrollToQuestionByIdFunc: any;
  setActiveQuestionState: (question: any, questionSet: any) => void;
  paginatedResultsManager: any;
  questionSets: List<QuestionList>;
  showQuestionArea: boolean;
}

const ContentDiscoveryReviewQuestionsList = ({
  activeQuestion,
  scrollToQuestionByIdFunc,
  setActiveQuestionState,
  paginatedResultsManager,
  showQuestionArea
}: Props) => {
  const cellMeasurerCache = useRef<CellMeasurerCache>();
  const listRef = useRef<VirtualizedList>(null);
  const orderByBtnRef = useRef(null);
  const scrollPosition = useRef(0);
  const [isLoadingMoreQuestions, setIsLoadingMoreQuestions] = useState(false);

  const {
    autoSave,
    draftSort,
    setDraftSort,
    draftId,
    draftQuestionsQuery,
    removeQuestionSet,
    setCurrentPage,
    setDraftHasChanges,
    selectedQuestionSets,
    setDraftHasSavedDuringSession,
    totalQuestionCount,
    setSelectedQuestionSets
  } = useContext(ContentDiscoveryContext);

  const {trackEvent} = useTracking({event: 'click'});

  const questionSets = autoSave ? paginatedResultsManager.getResults() : selectedQuestionSets;

  // Exists in all question lists
  useEffect(() => {
    cellMeasurerCache.current = new CellMeasurerCache({
      fixedWidth: true,
      minHeight: 30
    });
  }, []);

  // Exists in all question lists
  const smoothScrollToPosition = useCallback(
    (endPos, startPos = scrollPosition.current, startTime = Date.now()) => {
      const duration = 1000;
      const elapsed = Date.now() - startTime;
      const nextPos = easeInOutCirc(elapsed, startPos, endPos - startPos, duration);
      listRef.current?.scrollToPosition(nextPos);
      if (elapsed < duration) {
        global.requestAnimationFrame(() => {
          smoothScrollToPosition(endPos, startPos, startTime);
        });
      }
    },
    []
  );

  // Exists in all question lists
  useEffect(() => {
    const scrollToQuestion = (questionSetId) => {
      const index = questionSets.findIndex((questionSet) => questionSet!.getId() === questionSetId);
      if (index === undefined) {
        return;
      }
      const offset = listRef!.current!.getOffsetForRow({
        alignment: 'start',
        index
      });
      smoothScrollToPosition(offset);
    };

    scrollToQuestionByIdFunc.current = scrollToQuestion;
  }, [scrollToQuestionByIdFunc, questionSets, smoothScrollToPosition]);

  // Handles fetching more questions
  // Exists in all question lists
  const handleRowsRendered = debounce(async ({stopIndex}) => {
    if (!autoSave) return;
    const isLastQuestionScrolledIntoView = stopIndex + 1 === questionSets.size;
    if (!paginatedResultsManager.isRequestInFlight) {
      if (isLastQuestionScrolledIntoView) {
        setIsLoadingMoreQuestions(true);
        await paginatedResultsManager.fetchNextPage();
        setIsLoadingMoreQuestions(false);
      }
    }
  }, 250);

  const updateScrollPosition = ({scrollTop}) => {
    scrollPosition.current = scrollTop;
  };
  // Re-initializes results manager to force a refetch after invalidation
  const onSuccess = () => paginatedResultsManager.initialize(getDraftQuestionSetsQuery(draftId));

  // to support broader multi select 'oldIndex' is a set
  // This feature only supports single select so we can safely take 'first' from the set
  const onSortEnd = (oldIndex, newIndex) => {
    if (autoSave) {
      if (oldIndex.first() !== newIndex) {
        paginatedResultsManager.moveQuestionSetToIndex(oldIndex, newIndex);
        // we don't want to invalidate the resource here since we are not saving any changes yet
        saveReordering();
      }
    } else {
      const itemToMove = selectedQuestionSets.get(oldIndex.first());
      const newQuestionSets = selectedQuestionSets
        .delete(oldIndex.first())
        .insert(newIndex, itemToMove);
      setSelectedQuestionSets(newQuestionSets);
    }
  };

  const hasActiveQuestion = useMemo(
    () => !activeQuestion || Object.keys(activeQuestion).length > 0,
    [activeQuestion]
  );

  const saveReordering = async () => {
    const payload = {
      data: paginatedResultsManager
        .getResults()
        .map((questionSet, index) => {
          if (questionSet) {
            return {
              type: 'question_sets_v1',
              id: questionSet.getId(),
              meta: {
                position: index
              }
            };
          }
          return null;
        })
        .toArray()
    };
    try {
      setDraftHasChanges(true);
      await genericMandarkRequest(
        'post',
        {
          resourcePath: ['draft_assignments_v1', draftId, 'relationships', 'question_sets_v1']
        },
        payload
      );
      addToast({
        type: 'success',
        title: 'Success!',
        message: 'Your changes have been saved.'
      });
      setDraftHasSavedDuringSession(true);
      invalidatePartialInterest({resourcePath: draftQuestionsQuery.done().resourcePath});
    } catch (e) {
      addToast({
        type: 'danger',
        title: 'Error',
        message: 'Something went wrong with saving your changes.'
      });
    }
  };

  // Renders individual list items
  // Todo: Consider pulling out into own component to trim down lines in this component
  const rowRenderer = ({index, key, parent, style}) => {
    const questionSet = questionSets.get(index);
    // const subject = questionSet.getSubject();
    const questionSetDifficulty = getDifficultyValueFromInt(
      questionSet?.getQuestions()!.get(0)?.getDifficulty(),
      true
    );
    const questionSetType = questionSet
      ?.getQuestions()!
      .first()
      ?.getQuestionType()!
      .replace(/-/g, ' ');

    const sortHandle = (
      <IconButton
        label={`Reorder icon: select ${questionSet
          ?.getQuestions()
          .get(0)
          .getTitle()} to move its position.`}
        color='secondary'
        variant='outlined'
        roundness='square'
        size='s'
        icon={['far', 'arrows-up-down']}
      />
    );
    const cancelHandle = (
      <IconButton
        label='cancel reordering'
        roundness='square'
        size='s'
        variant='solid'
        color='primary'
        icon='times'
      />
    );
    return (
      <CellMeasurer
        cache={cellMeasurerCache.current!}
        columnIndex={0}
        key={key}
        rowIndex={index}
        parent={parent}
      >
        <SortableContainerContext.Consumer>
          {(value) => {
            const isActive =
              hasActiveQuestion && questionSet?.getId() === activeQuestion.getQuestionSet().getId();
            return (
              // Sortable Element handles most of the logic out of the three pieces
              // It consumes all of the values emmitted from the context consumer
              <SortableElement
                {...value}
                index={index}
                key={key}
                elementHeight='50px'
                customCancelHandle={cancelHandle}
                customSortHandle={sortHandle}
              >
                {({SortHandler, HoverArea, recentlyMovedIndices}) => {
                  return (
                    <ListGroupItem
                      id={questionSet?.getId()}
                      as='button'
                      tabIndex={0}
                      paddingLevel={1}
                      onClick={() => {
                        setActiveQuestionState(questionSet?.getQuestions().first(), questionSet);
                      }}
                      style={style}
                      className={classnames(
                        'question-v2 u-cursor_pointer review-questions-list__row',
                        {
                          'sortable-element__moved': recentlyMovedIndices?.includes(index)
                        }
                      )}
                    >
                      <HoverArea />
                      <div
                        className={classnames(
                          'u-display_flex u-align-items_center u-flex_1 cd-question-list__question u-gap_1',
                          {
                            'cd-question-list__question--selected': isActive && showQuestionArea
                          }
                        )}
                      >
                        <div className='u-flex_1'>
                          <Text
                            className='review-questions-list__question-item'
                            as={MarkdownRendererV2}
                            text={questionSet?.getQuestions()!.get(0)?.getTitle()}
                            size='m'
                            bold
                          />
                          <div className='cd-question-list__question-metadata'>
                            <Text
                              as='div'
                              className={`cd-question-list__question-metadata-text cd-questionlist__difficulty cd-questionlist__difficulty-${questionSetDifficulty}`}
                            >
                              {questionSetDifficulty}
                            </Text>
                            <Text as='div' className='cd-question-list__question-metadata-text'>
                              {questionSetType}
                            </Text>
                          </div>
                        </div>

                        {questionSets.size > 1 && <SortHandler />}

                        <WithTooltip content='Remove'>
                          <IconButton
                            color='secondary'
                            variant='outlined'
                            roundness='square'
                            size='s'
                            label={`Remove ${questionSet?.getQuestions()!.get(0)?.getTitle()}`}
                            icon={['far', 'trash']}
                            onClick={(e) => {
                              trackEvent({
                                type: 'icon',
                                component: 'review questions list',
                                content: 'delete'
                              });
                              removeQuestionSet(e, questionSet, onSuccess);
                            }}
                          />
                        </WithTooltip>
                        <Icon icon='chevron-right' className='u-mar-l_1' />
                      </div>
                      {/* <div
                        className={classnames(
                          {
                            'clickable-indicator--active': isActive && showQuestionArea
                          },
                          'clickable-indicator'
                        )}
                      /> */}
                    </ListGroupItem>
                  );
                }}
              </SortableElement>
            );
          }}
        </SortableContainerContext.Consumer>
      </CellMeasurer>
    );
  };

  const sortOptions = {
    difficulty: 'difficulty',
    // question_type: 'question_type',
    name: 'name'
  };

  const handleSort = (sortVal) => {
    let sort = sortVal;
    if (draftSort && draftSort.includes(sortVal) && !draftSort.includes('-')) {
      sort = `-${sort}`;
    }
    setDraftSort(sort);
  };

  useEffect(() => {
    // manually sort questions list if no draftQuery to update
    if (autoSave) return;

    const isNegativeSort = draftSort?.includes('-');
    const option = draftSort?.replace('-', '');

    if (option === sortOptions.name) {
      const nameSort = sortQuestionSetHandler((question) => question.getTitle(), isNegativeSort);
      setSelectedQuestionSets((questionSets) => {
        const sortedQuestions = questionSets.toArray().sort(nameSort);
        return List(sortedQuestions);
      });
    }
    if (option === sortOptions.difficulty) {
      const difficultySort = sortQuestionSetHandler(
        (question) => question.getDifficulty(),
        isNegativeSort
      );
      setSelectedQuestionSets((questionSets) => {
        const sortedQuestions = questionSets.toArray().sort(difficultySort);
        return List(sortedQuestions);
      });
    }
    // }
  }, [draftSort, setSelectedQuestionSets, autoSave]);

  const questionCount = autoSave ? totalQuestionCount : questionSets.size;

  const questionText =
    questionCount > 0 ? `${questionCount} Assignment ${pluralize('question', questionCount)}` : '';

  return (
    <div className='u-display_flex u-justify-content_center cd-question-list__column_review'>
      <SortableContainerProvider onSortEnd={onSortEnd}>
        <div className='content-discovery__review-question-list'>
          <ListGroup className='u-display_flex u-flex-direction_column u-width_100pc'>
            <ListGroupItem className='u-display_flex u-pad_3 u-align-items_center'>
              <Text bold className='u-text-transform_capitalize u-mar-r_2 u-mar-tb_0'>
                {questionText}
              </Text>
              <Button
                className='u-width_fit-content'
                variant='outlined'
                onClick={() => {
                  setCurrentPage('add');
                  trackEvent({
                    type: 'button',
                    component: 'review questions list toolbar',
                    content: 'add more'
                  });
                }}
              >
                Add More <Icon className='u-pad-l_1' icon='plus' />
              </Button>
              <WithToggle className='u-mar-l_auto'>
                {({on, onClick}) => (
                  <>
                    <Button
                      ref={orderByBtnRef}
                      variant='text'
                      className='u-mar-r_1'
                      onClick={onClick}
                    >
                      Order By {draftSort && `: ${upperFirst(draftSort.replace('-', ''))}`}
                      <Icon className='u-pad-l_1' icon='caret-down' />
                    </Button>
                    {on ? (
                      <Dropdown.Tray
                        className='cd-question-list_pick-for-me'
                        target={orderByBtnRef.current}
                      >
                        <Card>
                          <div className='u-display_flex u-flex-direction_column u-pad_1'>
                            {Object.values(sortOptions).map((option) => {
                              const isSortOption = draftSort && draftSort.includes(option);
                              const isNegativeSort = isSortOption && draftSort?.includes('-');
                              return (
                                <Button
                                  className='u-display_flex u-justify-content_space-between u-font-weight_normal'
                                  color='secondary'
                                  onClick={() => {
                                    handleSort(option);
                                    trackEvent({
                                      type: 'button',
                                      component: 'review questions list toolbar',
                                      content: `sort by ${option}`
                                    });
                                  }}
                                  variant='text'
                                >
                                  <Text>{upperFirst(option)}</Text>
                                  {isSortOption ? (
                                    <Icon
                                      className='u-mar-l_1'
                                      icon={isNegativeSort ? 'arrow-down' : 'arrow-up'}
                                    />
                                  ) : null}
                                </Button>
                              );
                            })}
                          </div>
                        </Card>
                      </Dropdown.Tray>
                    ) : null}
                  </>
                )}
              </WithToggle>
              <Button
                variant='text'
                color='destructive'
                onClick={(e) => {
                  removeQuestionSet(e, questionSets, onSuccess);
                  trackEvent({
                    type: 'button',
                    component: 'review questions list header',
                    content: 'remove all'
                  });
                }}
              >
                Remove all
              </Button>
            </ListGroupItem>
            <div className='u-flex-grow_1 u-overflow_hidden'>
              <AutoSizer>
                {({width, height}) => (
                  <VirtualizedList
                    ref={listRef}
                    height={height}
                    width={width}
                    rowHeight={cellMeasurerCache.current?.rowHeight || 100}
                    onRowsRendered={handleRowsRendered}
                    rowCount={questionSets.size}
                    rowRenderer={rowRenderer}
                    onScroll={updateScrollPosition}
                    tabIndex={-1}
                  />
                )}
              </AutoSizer>
              {isLoadingMoreQuestions ? (
                <LoadingSpinner className='questions-list-v2__loading-spinner' size={2} />
              ) : null}
            </div>
            {autoSave && hasActiveQuestion && (
              <ListGroupItem footer paddingLevel={0} className='questions-list-v2__footer'>
                <QuestionsListFooter
                  activeQuestion={activeQuestion}
                  setActiveQuestionState={setActiveQuestionState}
                  scrollToQuestionById={scrollToQuestionByIdFunc}
                  paginatedQuestionsListManager={paginatedResultsManager}
                />
              </ListGroupItem>
            )}
          </ListGroup>
        </div>
      </SortableContainerProvider>
    </div>
  );
};

export default ContentDiscoveryReviewQuestionsList;

const sortQuestionSetHandler = (getValue, reverseSort = false) => {
  return (questionSetA, questionSetB) => {
    const questionA = questionSetA?.getQuestions().get(0);
    const questionB = questionSetB?.getQuestions().get(0);
    if (!questionA && !questionB) return 0;
    if (!questionA) return reverseSort ? 1 : -1;
    if (!questionB) return reverseSort ? -1 : 1;
    if (getValue(questionA) < getValue(questionB)) {
      return reverseSort ? 1 : -1;
    }
    if (getValue(questionA) > getValue(questionB)) {
      return reverseSort ? -1 : 1;
    }
    return 0;
  };
};
